import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Send, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface MessageTemplate {
  id: string;
  name: string;
  content: string;
  variables: string[];
  category: 'marketing' | 'utility' | 'authentication';
  language: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  platform: 'whatsapp' | 'facebook' | 'instagram' | 'telegram';
  created_at: string;
  submitted_at?: string;
  approved_at?: string;
  usage_count: number;
  meta_template_id?: string;
  submission_error?: string;
}

const TemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Mock data
  useEffect(() => {
    const mockTemplates: MessageTemplate[] = [
      {
        id: '1',
        name: 'Welcome Message',
        content: 'Hello {{name}}, welcome to {{company}}! We\'re excited to have you on board. Your account is now active.',
        variables: ['name', 'company'],
        category: 'utility',
        language: 'en',
        status: 'approved',
        platform: 'whatsapp',
        created_at: '2024-01-01T00:00:00Z',
        approved_at: '2024-01-02T10:00:00Z',
        usage_count: 45
      },
      {
        id: '2',
        name: 'Promotional Offer',
        content: 'Hi {{name}}! 🎉 Special offer for you: {{discount}}% off on your next purchase. Use code {{code}}. Valid until {{expiry_date}}.',
        variables: ['name', 'discount', 'code', 'expiry_date'],
        category: 'marketing',
        language: 'en',
        status: 'pending',
        platform: 'whatsapp',
        created_at: '2024-01-10T00:00:00Z',
        submitted_at: '2024-01-11T09:00:00Z',
        usage_count: 0
      },
      {
        id: '3',
        name: 'Order Confirmation',
        // eslint-disable-next-line no-template-curly-in-string
        content: 'Your order #{{order_id}} has been confirmed! Total: ${{amount}}. Estimated delivery: {{delivery_date}}. Track your order: {{tracking_link}}',
        variables: ['order_id', 'amount', 'delivery_date', 'tracking_link'],
        category: 'utility',
        language: 'en',
        status: 'rejected',
        rejection_reason: 'Template contains promotional content but is categorized as utility. Please recategorize as marketing.',
        platform: 'whatsapp',
        created_at: '2024-01-05T00:00:00Z',
        submitted_at: '2024-01-06T14:00:00Z',
        usage_count: 0
      }
    ];
    setTemplates(mockTemplates);
  }, []);

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === '' || template.category === selectedCategory;
    const matchesStatus = selectedStatus === '' || template.status === selectedStatus;
    return matchesCategory && matchesStatus;
  });

  const testMetaAPI = async () => {
    try {
      const whatsappConfig = JSON.parse(localStorage.getItem('whatsapp_config') || '{}');
      
      if (!whatsappConfig.app_id || !whatsappConfig.permanent_token) {
        toast.error('WhatsApp configuration missing. Please configure your WhatsApp Business API credentials first.');
        return;
      }

      toast.loading('Testing Meta API connection...', { id: 'meta-test' });

      // 1. Test basic API access
      const accessResponse = await fetch(`https://graph.facebook.com/v18.0/${whatsappConfig.app_id}?access_token=${whatsappConfig.permanent_token}`);
      const accessData = await accessResponse.json();
      
      if (!accessResponse.ok) {
        throw new Error(`API Access failed: ${accessData.error?.message}`);
      }

      // 2. Test template creation with a simple demo template
      const testTemplate = {
        name: 'demo_test_template_' + Date.now(),
        language: 'en',
        category: 'UTILITY',
        components: [
          {
            type: 'BODY',
            text: 'This is a test template created at {{1}}. If you see this, the API is working!',
            example: {
              body_text: [[new Date().toLocaleString()]]
            }
          }
        ]
      };

      const templateResponse = await fetch(`https://graph.facebook.com/v18.0/${whatsappConfig.app_id}/message_templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${whatsappConfig.permanent_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testTemplate)
      });

      const templateData = await templateResponse.json();

      if (templateResponse.ok) {
        toast.success(`✅ Meta API Test Successful! Template ID: ${templateData.id}`, { id: 'meta-test' });
        
        // Add the test template to local state
        const newTemplate: MessageTemplate = {
          id: Date.now().toString(),
          name: 'API Test Template',
          content: 'This is a test template created at {{timestamp}}. If you see this, the API is working!',
          variables: ['timestamp'],
          category: 'utility',
          language: 'en',
          status: 'pending',
          platform: 'whatsapp',
          created_at: new Date().toISOString(),
          submitted_at: new Date().toISOString(),
          usage_count: 0,
          meta_template_id: templateData.id
        };
        
        setTemplates(prev => [newTemplate, ...prev]);
      } else {
        // Check if it's just a duplicate template error
        if (templateData.error?.code === 100 && templateData.error?.error_subcode === 2388026) {
          toast.success('✅ Meta API is working! (Test template already exists)', { id: 'meta-test' });
        } else {
          throw new Error(`Template submission failed: ${templateData.error?.message}`);
        }
      }

    } catch (error: any) {
      console.error('Meta API test error:', error);
      toast.error(`❌ Meta API Test Failed: ${error.message}`, { id: 'meta-test' });
    }
  };

  const CreateTemplateForm: React.FC<{ template?: MessageTemplate; onClose: () => void }> = ({ 
    template, onClose 
  }) => {
    const [formData, setFormData] = useState({
      name: template?.name || '',
      content: template?.content || '',
      category: template?.category || 'utility',
      language: template?.language || 'en',
      platform: template?.platform || 'whatsapp'
    });

    const [variables, setVariables] = useState<string[]>(template?.variables || []);
    const [preview, setPreview] = useState('');

    // Extract variables from content
    useEffect(() => {
      const variableRegex = /\{\{(\w+)\}\}/g;
      const matches = formData.content.match(variableRegex);
      if (matches) {
        const extractedVars = matches.map(match => match.replace(/[{}]/g, ''));
        setVariables(Array.from(new Set(extractedVars)));
      } else {
        setVariables([]);
      }
    }, [formData.content]);

    // Generate preview with sample data
    useEffect(() => {
      let previewText = formData.content;
      variables.forEach(variable => {
        const sampleData: { [key: string]: string } = {
          name: 'John Doe',
          company: 'Your Company',
          discount: '20',
          code: 'SAVE20',
          expiry_date: '2024-12-31',
          order_id: 'ORD12345',
          amount: '99.99',
          delivery_date: '2024-01-25',
          tracking_link: 'https://track.example.com/12345'
        };
        const sampleValue = sampleData[variable] || '[' + variable + ']';
        previewText = previewText.replace(new RegExp('\\{\\{' + variable + '\\}\\}', 'g'), sampleValue);
      });
      setPreview(previewText);
    }, [formData.content, variables]);

    const handleSave = () => {
      const templateData: Partial<MessageTemplate> = {
        ...formData,
        variables,
        status: 'draft',
        created_at: new Date().toISOString(),
        usage_count: 0
      };

      if (template) {
        setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, ...templateData } : t));
        toast.success('Template updated successfully');
      } else {
        const newTemplate: MessageTemplate = {
          id: Date.now().toString(),
          ...templateData
        } as MessageTemplate;
        setTemplates(prev => [newTemplate, ...prev]);
        toast.success('Template created successfully');
      }
      onClose();
    };

    const handleSubmitForApproval = async () => {
      try {
        // Get WhatsApp configuration from localStorage or context
        const whatsappConfig = JSON.parse(localStorage.getItem('whatsapp_config') || '{}');
        
        if (!whatsappConfig.app_id || !whatsappConfig.permanent_token) {
          toast.error('WhatsApp configuration missing. Please configure your WhatsApp Business API credentials first.');
          return;
        }

        const updatedTemplate = {
          ...formData,
          variables,
          status: 'pending' as const,
          submitted_at: new Date().toISOString()
        };

        // Submit template to Meta WhatsApp Business API
        const templatePayload = {
          name: formData.name.toLowerCase().replace(/\s+/g, '_'), // Meta requires lowercase with underscores
          language: formData.language,
          category: formData.category.toUpperCase(),
          components: [
            {
              type: 'BODY',
              text: formData.content,
              example: variables.length > 0 ? {
                body_text: [variables.map(v => `Sample ${v}`)]
              } : undefined
            }
          ]
        };

        // Call Meta API
        const response = await fetch(`https://graph.facebook.com/v18.0/${whatsappConfig.app_id}/message_templates`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${whatsappConfig.permanent_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(templatePayload)
        });

        const result = await response.json();

        if (response.ok) {
          // Update local template with Meta template ID
          const finalTemplate = {
            ...updatedTemplate,
            meta_template_id: result.id,
            status: 'pending' as const
          };

          if (template) {
            setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, ...finalTemplate } : t));
          } else {
            const newTemplate: MessageTemplate = {
              id: Date.now().toString(),
              created_at: new Date().toISOString(),
              usage_count: 0,
              ...finalTemplate
            } as MessageTemplate;
            setTemplates(prev => [newTemplate, ...prev]);
          }
          
          toast.success('Template submitted to Meta for approval successfully!');
        } else {
          throw new Error(result.error?.message || 'Failed to submit template to Meta');
        }
      } catch (error: any) {
        console.error('Template submission error:', error);
        
        // Still save locally as pending even if Meta API fails
        const updatedTemplate = {
          ...formData,
          variables,
          status: 'pending' as const,
          submitted_at: new Date().toISOString(),
          submission_error: error.message
        };

        if (template) {
          setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, ...updatedTemplate } : t));
        } else {
          const newTemplate: MessageTemplate = {
            id: Date.now().toString(),
            created_at: new Date().toISOString(),
            usage_count: 0,
            ...updatedTemplate
          } as MessageTemplate;
          setTemplates(prev => [newTemplate, ...prev]);
        }
        
        toast.error(`Failed to submit to Meta: ${error.message}. Template saved locally as pending.`);
      }
      
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">
              {template ? 'Edit Template' : 'Create Message Template'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter template name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="utility">Utility</option>
                    <option value="marketing">Marketing</option>
                    <option value="authentication">Authentication</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.category === 'utility' && 'For account updates, order confirmations, etc.'}
                    {formData.category === 'marketing' && 'For promotional messages, offers, etc.'}
                    {formData.category === 'authentication' && 'For OTP, verification codes, etc.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="zh">Chinese</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value as any })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="telegram">Telegram</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message Content
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={6}
                  placeholder="Enter your message template. Use {{variable}} for dynamic content."
                />
                <div className="mt-1 text-sm text-gray-500">
                  Character count: {formData.content.length} | Use {`{{variable}}`} for dynamic content
                </div>
              </div>

              {variables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Variables Found ({variables.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {variables.map((variable, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">Template Guidelines</h4>
                <ul className="text-xs text-yellow-700 space-y-1">
                  <li>• Marketing templates require pre-approval from Facebook</li>
                  <li>• Utility templates are for transactional messages only</li>
                  <li>• Authentication templates are for OTP and verification</li>
                  <li>• Templates cannot be edited once approved</li>
                  <li>• Variables must be wrapped in double curly braces: {`{{variable}}`}</li>
                </ul>
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Live Preview
              </label>
              <div className="border border-gray-300 rounded-md p-4 bg-gray-50 min-h-40">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      W
                    </div>
                    <div className="ml-2">
                      <div className="text-sm font-medium">Your Business</div>
                      <div className="text-xs text-gray-500">WhatsApp Business</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">
                    {preview || 'Your message preview will appear here...'}
                  </div>
                  {variables.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-xs text-gray-500">
                        Sample data is used for preview. Actual values will be dynamic.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-8">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Save as Draft
            </button>
            <button
              onClick={handleSubmitForApproval}
              disabled={!formData.name || !formData.content}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center"
            >
              <Send size={16} className="mr-1" />
              Submit for Approval
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending': return <Clock className="w-5 h-5 text-yellow-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    setTemplates(prev => prev.filter(t => t.id !== templateId));
    toast.success('Template deleted successfully');
  };

  return (
    <div className="p-6 bg-white">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Template Manager</h2>
          <p className="text-gray-600">Create and manage message templates for WhatsApp Business API</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={testMetaAPI}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center"
          >
            <CheckCircle size={16} className="mr-1" />
            Test Meta API
          </button>
          <button
            onClick={() => setShowCreateTemplate(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <Plus size={16} className="mr-1" />
            Create Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Categories</option>
          <option value="utility">Utility</option>
          <option value="marketing">Marketing</option>
          <option value="authentication">Authentication</option>
        </select>
        
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                <div className="flex items-center mt-1">
                  {getStatusIcon(template.status)}
                  <span className={`ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                    {template.status}
                  </span>
                </div>
              </div>
              
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    setEditingTemplate(template);
                    setShowCreateTemplate(true);
                  }}
                  className="text-gray-600 hover:text-gray-700"
                  title="Edit Template"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDeleteTemplate(template.id)}
                  className="text-red-600 hover:text-red-700"
                  title="Delete Template"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-600 line-clamp-3">
                  {template.content}
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  template.category === 'marketing' ? 'bg-purple-100 text-purple-800' :
                  template.category === 'utility' ? 'bg-blue-100 text-blue-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {template.category}
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {template.language}
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {template.platform}
                </span>
              </div>

              {template.variables.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">Variables:</div>
                  <div className="flex flex-wrap gap-1">
                    {template.variables.map((variable, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700"
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center text-sm text-gray-500 pt-3 border-t">
                <span>Used {template.usage_count} times</span>
                <span>{new Date(template.created_at).toLocaleDateString()}</span>
              </div>

              {template.status === 'rejected' && template.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <div className="text-xs text-red-800">
                    <strong>Rejection Reason:</strong> {template.rejection_reason}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first message template
          </p>
        </div>
      )}

      {/* Create/Edit Template Modal */}
      {showCreateTemplate && (
        <CreateTemplateForm
          template={editingTemplate || undefined}
          onClose={() => {
            setShowCreateTemplate(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
};

export default TemplateManager; 